import React, { createContext, useState, useEffect, useContext } from 'react';
import { supabase } from './supabaseClient';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// 🔑 Allowed roles for admin dashboard access
const ALLOWED_ROLES = ['admin', 'owner', 'manager'];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null); // 🆕 Store full user profile
  const [loading, setLoading] = useState(true);

  // 🔒 Check if user has allowed role and return profile
  const checkUserRole = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      if (error) {
        console.error('Error checking role:', error);
        return { allowed: false, profile: null };
      }
      
      const role = data?.role?.toLowerCase();
      const isAllowed = ALLOWED_ROLES.includes(role);
      console.log('🔒 Role check:', data?.role, 'isAllowed:', isAllowed);
      return { allowed: isAllowed, profile: data };
    } catch (err) {
      console.error('Role check failed:', err);
      return { allowed: false, profile: null };
    }
  };

  useEffect(() => {
    // Check active sessions and sets the user
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        // 🔒 Verify role before allowing access
        const { allowed, profile } = await checkUserRole(session.user.id);
        if (allowed) {
          setUser(session.user);
          setUserProfile(profile); // 🆕 Store profile
        } else {
          console.log('🚫 Access denied - not an allowed role');
          await supabase.auth.signOut();
          setUser(null);
          setUserProfile(null);
        }
      } else {
        setUser(null);
        setUserProfile(null);
      }
      setLoading(false);
    });

    // Listen for changes on auth state (signed in, signed out, etc.)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (_event === 'SIGNED_OUT') {
        setUser(null);
        setUserProfile(null);
        setLoading(false);
      } else if (session?.user) {
        // 🔒 Verify role on auth state change
        checkUserRole(session.user.id).then(({ allowed, profile }) => {
          if (allowed) {
            setUser(session.user);
            setUserProfile(profile); // 🆕 Store profile
          } else {
            console.log('🚫 Access denied on auth change - not an allowed role');
            supabase.auth.signOut();
            setUser(null);
            setUserProfile(null);
          }
          setLoading(false);
        });
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signInWithEmail = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) {
      return { data: null, error };
    }
    
    // 🔒 CHECK ROLE after successful auth
    if (data?.user) {
      const { allowed, profile } = await checkUserRole(data.user.id);
      if (!allowed) {
        // Sign out immediately if not allowed role
        await supabase.auth.signOut();
        return { 
          data: null, 
          error: { message: 'Access denied. This portal is for administrators only.' }
        };
      }
      // 🆕 Store profile on successful login
      setUserProfile(profile);
    }
    
    return { data, error: null };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    setUserProfile(null); // 🆕 Clear profile on sign out
    return { error };
  };

  const value = {
    user,
    userProfile, // 🆕 Expose userProfile
    loading,
    signInWithEmail,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
